/**
 * Chat input area component.
 * Textarea with send button.
 */

// Classic stroked send arrow. Drawn with stroke=currentColor so --send-icon-color drives it.
const SEND_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" fill="none">
  <path d="M2.5 9h11M9 4.5L13.5 9 9 13.5" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

export interface InputAreaProps {
  placeholder: string;
  onSend: (message: string) => void;
  disabled?: boolean;
}

export interface InputAreaElements {
  container: HTMLDivElement;
  textarea: HTMLTextAreaElement;
  sendButton: HTMLButtonElement;
}

export function createInputArea(props: InputAreaProps): InputAreaElements {
  const container = document.createElement('div');
  container.className = 'input-area';

  const inputContainer = document.createElement('div');
  inputContainer.className = 'input-container';

  const textarea = document.createElement('textarea');
  textarea.className = 'message-input';
  textarea.placeholder = props.placeholder;
  textarea.rows = 1;
  textarea.disabled = props.disabled || false;
  textarea.setAttribute('aria-label', 'Type your message');

  const sendButton = document.createElement('button');
  sendButton.className = 'send-button';
  sendButton.type = 'button';
  sendButton.setAttribute('aria-label', 'Send message');
  sendButton.innerHTML = SEND_ICON;

  // Auto-resize textarea
  textarea.addEventListener('input', () => {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  });

  // Send on Enter (Shift+Enter for newline)
  textarea.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (textarea.value.trim()) {
        props.onSend(textarea.value.trim());
        textarea.value = '';
        textarea.style.height = 'auto';
      }
    }
  });

  // Send button click
  sendButton.addEventListener('click', () => {
    if (textarea.value.trim()) {
      props.onSend(textarea.value.trim());
      textarea.value = '';
      textarea.style.height = 'auto';
      textarea.focus();
    }
  });

  inputContainer.appendChild(textarea);
  inputContainer.appendChild(sendButton);
  container.appendChild(inputContainer);

  return { container, textarea, sendButton };
}

export function setInputDisabled(elements: InputAreaElements, disabled: boolean): void {
  elements.textarea.disabled = disabled;
}

export function focusInput(elements: InputAreaElements): void {
  elements.textarea.focus();
}
